/**
 * Textos da superfície pública, por idioma.
 *
 * O idioma segue a clínica (ver `idioma.ts` em @ac/shared). Mas há um problema
 * de arranque: os ecrãs de erro — link inválido, expirado, já usado — aparecem
 * **antes** de haver resposta da API, e nessa altura ainda não sabemos de que
 * clínica se trata. Por isso o link remoto leva também `?l=es`, que é só uma
 * pista de idioma: não é um id nem um dado pessoal, e não viola o §10.
 *
 * Assim que a sessão carrega, manda o `idioma` que vem do servidor — a pista do
 * URL é conveniência, nunca autoridade.
 */

import { idiomaValido, type Idioma } from '@ac/shared';

interface Textos {
	aCarregar: string;
	linkIndisponivel: string;
	linkInvalido: string;
	semToken: string;
	linkUsado: string;
	linkExpirado: string;
	falhaAssinatura: string;
	assinada: string;
	registadas: (n: number) => string;
	titulo: string;
	nif: string;
	distribuidores: string;
	notaDistribuidores: string;
	declaracao: string;
	versao: (v: string) => string;
	assinatura: string;
	confirmacao: string;
	aRegistar: string;
	assinar: string;
}

const PT: Textos = {
	aCarregar: 'A carregar…',
	linkIndisponivel: 'Link indisponível',
	linkInvalido: 'Este link não é válido.',
	semToken: 'Este endereço não inclui um link de assinatura válido.',
	linkUsado: 'Este link já foi utilizado. A autorização está assinada.',
	linkExpirado: 'Este link expirou. Peça um novo ao seu contacto na Hifarmax.',
	falhaAssinatura: 'Não foi possível registar a assinatura.',
	assinada: 'Autorização assinada',
	registadas: (n) =>
		`Ficaram registadas ${n} autorizaç${n === 1 ? 'ão' : 'ões'}, uma por distribuidor. ` +
		'Este link já não pode ser reutilizado.',
	titulo: 'Autorização de partilha de consumos',
	nif: 'NIF',
	distribuidores: 'Distribuidores abrangidos',
	notaDistribuidores:
		'É gerada uma autorização distinta por distribuidor, cada uma nomeando apenas o seu. ' +
		'Vigoram até serem revogadas.',
	declaracao: 'Declaração',
	versao: (v) => `Versão ${v}`,
	assinatura: 'Assinatura',
	confirmacao: 'Li a declaração e autorizo a partilha dos dados de consumos nos termos acima.',
	aRegistar: 'A registar…',
	assinar: 'Assinar autorização'
};

const ES: Textos = {
	aCarregar: 'Cargando…',
	linkIndisponivel: 'Enlace no disponible',
	linkInvalido: 'Este enlace no es válido.',
	semToken: 'Esta dirección no incluye un enlace de firma válido.',
	linkUsado: 'Este enlace ya se ha utilizado. La autorización está firmada.',
	linkExpirado: 'Este enlace ha caducado. Solicite uno nuevo a su contacto en Hifarmax.',
	falhaAssinatura: 'No se ha podido registrar la firma.',
	assinada: 'Autorización firmada',
	registadas: (n) =>
		`Se han registrado ${n} autorizaci${n === 1 ? 'ón' : 'ones'}, una por distribuidor. ` +
		'Este enlace ya no puede reutilizarse.',
	titulo: 'Autorización de comunicación de datos de consumo',
	nif: 'NIF/CIF',
	distribuidores: 'Distribuidores incluidos',
	notaDistribuidores:
		'Se genera una autorización distinta por distribuidor, cada una nombrando únicamente al suyo. ' +
		'Permanecen en vigor hasta su revocación.',
	declaracao: 'Declaración',
	versao: (v) => `Versión ${v}`,
	assinatura: 'Firma',
	confirmacao: 'He leído la declaración y autorizo la comunicación de los datos de consumo en los términos anteriores.',
	aRegistar: 'Registrando…',
	assinar: 'Firmar autorización'
};

export function textos(idioma: Idioma): Textos {
	return idioma === 'es' ? ES : PT;
}

/** Pista de idioma no URL, para os ecrãs que aparecem antes da resposta da API. */
export function idiomaDoUrl(): Idioma {
	return idiomaValido(new URLSearchParams(location.search).get('l'));
}
